import { useState, type FormEvent, type ReactNode } from "react";
import {
  AddressAutocompleteInput,
  Alert,
  ApiError,
  Button,
  Checkbox,
  Input,
  LANGUAGE_LABELS,
  MESSAGES,
  Modal,
  PREFERRED_LANGUAGE_VALUES,
  Select,
  US_STATES,
  apiFetch,
  caregiverInviteDefaults,
  checkZipCityState,
  cooldownMessage,
  flattenIssues,
  isCaregiverPhoneCollision,
  makeCaregiverInviteSchema,
  zipMismatchMessage,
  type CaregiverInviteFields,
  type MessageKey,
  type PortalCaregiver,
  type PortalCaregiverPut,
} from "@dearbill/shared";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** Inline English translator; the member app mounts no `I18nProvider`. */
const t = (k: MessageKey): string => MESSAGES.en[k];
const schema = makeCaregiverInviteSchema(t);

const PHONE_COLLISION =
  "This number is already on your account. Enter the caregiver's own phone number.";
const FORM_ERROR = "Please check the form and try again.";
const GENERIC_ERROR = "We couldn't send the invitation. Please try again.";
const CONSENT_LABEL =
  "I authorize this person to access and manage my DearBill account, including my medications and program enrollment.";

interface AddCaregiverModalProps {
  open: boolean;
  /** Portal session bearer for the add. */
  session: string;
  /** Refetches the dashboard once the invitation is sent (or a 409 says one already exists). */
  reload: () => void;
  /** Called when the add is rejected with 401. */
  onSignOut: () => void;
  onClose: () => void;
}

/**
 * The "Add caregiver" form in the shared `Modal`. The caller toggles `open`,
 * so the dialog animates out, and keys the form per opening, so every opening
 * starts blank. Validation is the shared caregiver-invite zod
 * schema on submit, with each issue under its field; a Smarty pick fills the
 * street, city, state and ZIP at once. Submit PUTs `PortalCaregiverPut` and,
 * on success, closes and refetches the dashboard, whose pending row replaces
 * the Add button. A 422 phone collision lands under the phone field, another
 * 422 and any other failure as a form-level alert, a 429 as the cooldown
 * wait, a 409 (a caregiver already exists) as close-and-refetch, and a 401
 * signs the member out.
 */
export function AddCaregiverModal({
  open,
  session,
  reload,
  onSignOut,
  onClose,
}: AddCaregiverModalProps): ReactNode {
  const [form, setForm] = useState<CaregiverInviteFields>(caregiverInviteDefaults);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Smarty embedded key, read at render time, not module scope, so tests can
  // stub the env. Empty, the street field is a plain input and the ZIP check
  // on submit is skipped.
  const smartyKey = import.meta.env.VITE_SMARTY_EMBEDDED_KEY ?? "";

  function update<K extends keyof CaregiverInviteFields>(
    key: K,
    value: CaregiverInviteFields[K],
  ): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateAddress(key: keyof CaregiverInviteFields["address"], value: string): void {
    setForm((prev) => ({ ...prev, address: { ...prev.address, [key]: value } }));
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const result = schema.safeParse(form);
    if (!result.success) {
      setErrors(flattenIssues(result.error.issues));
      return;
    }
    setErrors({});
    setFormError(null);
    setSaving(true);
    // ZIP ↔ city/state check after the schema passed; a mismatch lands under
    // the ZIP and stops the PUT.
    const { address } = result.data;
    if ((await checkZipCityState(smartyKey, address)) === "mismatch") {
      setErrors({
        "address.zip": zipMismatchMessage(t("err.zipMismatch"), address.city, address.state),
      });
      setSaving(false);
      return;
    }
    try {
      await apiFetch<PortalCaregiver>(
        "/api/portal/caregiver",
        { method: "PUT", body: { ...result.data } satisfies PortalCaregiverPut },
        { baseUrl: API_BASE, token: session },
      );
    } catch (err) {
      setSaving(false);
      if (!(err instanceof ApiError)) {
        setFormError(GENERIC_ERROR);
        return;
      }
      if (err.status === 401) {
        onSignOut();
        return;
      }
      if (err.status === 422) {
        if (isCaregiverPhoneCollision(err)) setErrors({ phone: PHONE_COLLISION });
        else setFormError(FORM_ERROR);
        return;
      }
      const cooldown = cooldownMessage(err);
      if (cooldown !== null) {
        setFormError(cooldown);
        return;
      }
      if (err.status !== 409) {
        setFormError(GENERIC_ERROR);
        return;
      }
    }
    onClose();
    reload();
  }

  return (
    <Modal open={open} title="Add caregiver" size="lg" onClose={onClose}>
      <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="First name"
            required
            autoComplete="off"
            value={form.firstName}
            onChange={(e) => update("firstName", e.target.value)}
            error={errors.firstName}
          />
          <Input
            label="Last name"
            required
            autoComplete="off"
            value={form.lastName}
            onChange={(e) => update("lastName", e.target.value)}
            error={errors.lastName}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Relationship"
            placeholder="e.g. Daughter"
            required
            autoComplete="off"
            value={form.relationship}
            onChange={(e) => update("relationship", e.target.value)}
            error={errors.relationship}
          />
          <Input
            label="Email"
            type="email"
            required
            autoComplete="off"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            error={errors.email}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Phone"
            type="tel"
            required
            autoComplete="off"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            error={errors.phone}
          />
          <Select
            label="Preferred language"
            value={form.preferredLanguage}
            onChange={(e) => {
              const picked = PREFERRED_LANGUAGE_VALUES.find((v) => v === e.target.value);
              if (picked !== undefined) update("preferredLanguage", picked);
            }}
          >
            {PREFERRED_LANGUAGE_VALUES.map((value) => (
              <option key={value} value={value}>
                {LANGUAGE_LABELS[value]}
              </option>
            ))}
          </Select>
        </div>
        <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
          <AddressAutocompleteInput
            label="Street address"
            required
            autoComplete="off"
            embeddedKey={smartyKey}
            value={form.address.street1}
            onChange={(e) => updateAddress("street1", e.target.value)}
            onSelect={(s) =>
              setForm((prev) => ({
                ...prev,
                address: {
                  ...prev.address,
                  street1: s.street1,
                  city: s.city,
                  state: s.state,
                  zip: s.zip,
                },
              }))
            }
            error={errors["address.street1"]}
          />
          <Input
            label="Apt / unit"
            autoComplete="off"
            value={form.address.street2}
            onChange={(e) => updateAddress("street2", e.target.value)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-[2fr_1fr_1fr]">
          <Input
            label="City"
            required
            autoComplete="off"
            value={form.address.city}
            onChange={(e) => updateAddress("city", e.target.value)}
            error={errors["address.city"]}
          />
          <Select
            label="State"
            required
            value={form.address.state}
            onChange={(e) => updateAddress("state", e.target.value)}
            error={errors["address.state"]}
          >
            <option value="">Select</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </Select>
          <Input
            label="ZIP"
            required
            inputMode="numeric"
            autoComplete="off"
            value={form.address.zip}
            onChange={(e) => updateAddress("zip", e.target.value)}
            error={errors["address.zip"]}
          />
        </div>
        <Checkbox
          label={CONSENT_LABEL}
          required
          checked={form.consent}
          onChange={(e) => update("consent", e.target.checked)}
          error={errors.consent}
        />
        {formError !== null && <Alert tone="error" title={formError} />}
        <div className="flex justify-end gap-3">
          <Button variant="ghost" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? "Sending…" : "Send invitation"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
